"use client";

import { useRef, useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import type {
  QuickPayInput,
  QuickPayOutput,
  QuickPayReceiptContext,
} from "@tillix/api-contract/client-schemas";
import { useT } from "@tillix/i18n/react";
import { api } from "@tillix/tx-core/orpc/client";
import { toastManager } from "@tillix/ui-atoms/toast";
import type { Type } from "arktype";
import type { DefaultValues, FieldValues } from "react-hook-form";
import { useForm, useFormState } from "react-hook-form";
import {
  isApprovedTransactionStatus,
  isNetworkError,
  pollUntilConfirmed,
} from "@/lib/payment-recovery/poll";
import {
  type PaymentStatus,
  SLOW_THRESHOLD_MS,
  STATUS_POLL_INTERVAL_MS,
  STATUS_POLL_MAX_MS,
} from "./quick-pay-config";

interface UseQuickPayFormParams<TValues extends FieldValues> {
  tenantId: string;
  externalId: string;
  nameOnAccount?: string;
  zipCode?: string;
  amount: number;
  onSuccess: (result: QuickPayOutput, context: QuickPayReceiptContext) => void;
  schema: Type;
  defaultValues: DefaultValues<TValues>;
  buildPayload: (values: TValues, clientRequestId: string) => QuickPayInput;
  buildReceiptContext: (
    values: TValues,
  ) => Omit<QuickPayReceiptContext, "accountNumber">;
}

export function useQuickPayForm<TValues extends FieldValues>({
  tenantId,
  externalId,
  nameOnAccount,
  zipCode,
  amount,
  onSuccess,
  schema,
  defaultValues,
  buildPayload,
  buildReceiptContext,
}: UseQuickPayFormParams<TValues>) {
  const { t } = useT();
  const clientRequestIdRef = useRef<string | null>(null);
  const slowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>("idle");

  const clearSlowTimer = () => {
    if (slowTimerRef.current) {
      clearTimeout(slowTimerRef.current);
      slowTimerRef.current = null;
    }
  };

  const form = useForm<TValues>({
    resolver: arktypeResolver(schema),
    mode: "onChange",
    defaultValues,
  });

  const { errors } = useFormState({ control: form.control });
  const rootError = errors.root?.message;

  const handleFormSubmit = async (values: TValues) => {
    // FIXME: better to generate this on the server instead. Math.random is cryptographically weak
    const clientRequestId =
      typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
          });
    clientRequestIdRef.current = clientRequestId;
    setPaymentStatus("processing");
    form.clearErrors("root");

    slowTimerRef.current = setTimeout(() => {
      setPaymentStatus("slow");
    }, SLOW_THRESHOLD_MS);

    const receiptCtx: QuickPayReceiptContext = {
      ...buildReceiptContext(values),
      accountNumber: externalId,
    };

    try {
      const result = await api.quickPay.post(
        buildPayload(values, clientRequestId),
      );
      clearSlowTimer();
      setPaymentStatus("idle");
      toastManager.add({
        type: "success",
        title: t("quickPay.paymentSubmittedSuccess"),
      });
      onSuccess(result, receiptCtx);
    } catch (err: unknown) {
      clearSlowTimer();
      await handlePaymentError(err, clientRequestId, receiptCtx);
    }
  };

  const handlePaymentError = async (
    err: unknown,
    clientRequestId: string,
    receiptCtx: QuickPayReceiptContext,
  ) => {
    if (isNetworkError(err)) {
      setPaymentStatus("checking");

      const outcome = await pollUntilConfirmed({
        checkOnce: async () => {
          const statusResult = await api.quickPay.status({
            tenantId,
            externalId,
            clientRequestId,
          });
          return statusResult.transaction ?? null;
        },
        isApproved: (tx) => isApprovedTransactionStatus(tx.statusCode),
        intervalMs: STATUS_POLL_INTERVAL_MS,
        maxMs: STATUS_POLL_MAX_MS,
      });

      if (outcome.status === "confirmed") {
        setPaymentStatus("idle");
        toastManager.add({
          type: "success",
          title: t("quickPay.paymentFound"),
          description: t("quickPay.paymentProcessedDespiteIssue"),
        });
        onSuccess(
          {
            transactionId: outcome.result.transactionId,
            statusDescription: outcome.result.statusDescription,
            statusCode: outcome.result.statusCode,
            transactionDate: outcome.result.transactionDate,
          },
          receiptCtx,
        );
        return;
      }

      setPaymentStatus("idle");
      form.setError("root", {
        message: t("quickPay.couldNotConfirmPayment"),
      });
      return;
    }

    setPaymentStatus("idle");
    form.setError("root", {
      message: t("quickPay.unexpectedError"),
    });
  };

  return {
    form,
    handleFormSubmit,
    rootError,
    paymentStatus,
    isSubmitting: form.formState.isSubmitting,
    isValid: form.formState.isValid,
  };
}
